// src/services/jwtLifecycleService.ts
import { api } from "@/api";
import { cookieService } from "./cookieService";
import { reportError, reportMessage } from "@/utils/sentry";

// JWT配置常量（对齐后端配置）
export const JWT_CONFIG = {
  ACCESS_TOKEN_DAYS: 5, // Access Token有效期：5天
  REFRESH_TOKEN_DAYS: 7, // Refresh Token有效期：7天
  REFRESH_WINDOW_DAYS: 2, // 刷新窗口期：2天（7-5=2）
} as const;

// 刷新策略配置
const REFRESH_STRATEGY = {
  // 第3天开始主动刷新（还有4天时间）
  START_DAY: 3,
  START_INTERVAL_HOURS: 12, // 每12小时检查一次

  // 第4天增加频率（还有3天时间）
  NORMAL_DAY: 4,
  NORMAL_INTERVAL_HOURS: 6, // 每6小时检查一次

  // 第5天紧急模式（还有2天时间）
  URGENT_DAY: 5,
  URGENT_INTERVAL_HOURS: 2, // 每2小时检查一次

  // 第6-7天最后机会（还有1-0天时间）
  CRITICAL_DAY: 6,
  CRITICAL_INTERVAL_MINUTES: 30, // 每30分钟检查一次

  // 用户活动时的即时刷新阈值
  ACTIVITY_REFRESH_THRESHOLD_HOURS: 8, // 8小时内有活动就检查刷新
} as const;

interface TokenResponse {
  access: string;
  refresh: string;
  expires_in?: number;
}

interface RefreshState {
  isRefreshing: boolean;
  lastRefreshTime: number;
  lastActivityTime: number;
  refreshPromise: Promise<boolean> | null;
  timers: NodeJS.Timeout[];
}

/** JWT 生命周期管理：主动刷新、分阶段频率、并发安全、活动触发、静默刷新 */
class JWTLifecycleService {
  private state: RefreshState = {
    isRefreshing: false,
    lastRefreshTime: 0,
    lastActivityTime: Date.now(),
    refreshPromise: null,
    timers: [],
  };

  private isStarted = false;

  /**
   * 启动JWT生命周期管理
   */
  public start(): void {
    if (this.isStarted) {
      console.debug("JWT lifecycle already started");
      return;
    }

    this.isStarted = true;
    this.state.lastActivityTime = Date.now();

    // 立即检查一次
    this.scheduleNextRefresh();

    // 监听用户活动
    this.startActivityMonitoring();

    console.log("JWT lifecycle management started");
    reportMessage("JWT生命周期管理已启动", "info");
  }

  /**
   * 停止JWT生命周期管理
   */
  public stop(): void {
    if (!this.isStarted) return;

    this.isStarted = false;
    this.clearAllTimers();
    this.stopActivityMonitoring();

    console.log("JWT lifecycle management stopped");
  }

  /**
   * 强制立即刷新token（同步入口）
   */
  public async forceRefresh(): Promise<boolean> {
    return this.silentBackgroundRefresh();
  }

  /**
   * 用户活动时调用
   */
  public onUserActivity(): void {
    this.state.lastActivityTime = Date.now();

    // 如果超过阈值时间未刷新，立即检查
    const hoursSinceLastRefresh =
      (Date.now() - this.state.lastRefreshTime) / (1000 * 60 * 60);
    if (
      hoursSinceLastRefresh >= REFRESH_STRATEGY.ACTIVITY_REFRESH_THRESHOLD_HOURS
    ) {
      this.scheduleImmediateRefresh();
    }
  }

  /**
   * 通知用户活动（别名方法，保持API一致性）
   */
  public notifyActivity(): void {
    this.onUserActivity();
  }

  /**
   * 后台静默刷新（核心方法）
   */
  public async silentBackgroundRefresh(): Promise<boolean> {
    // 防止并发刷新
    if (this.state.refreshPromise) {
      console.debug("Refresh already in progress, returning existing promise");
      return this.state.refreshPromise;
    }

    this.state.refreshPromise = this.performRefresh();

    try {
      const result = await this.state.refreshPromise;
      return result;
    } finally {
      this.state.refreshPromise = null;
    }
  }

  /**
   * 执行实际的刷新操作
   */
  private async performRefresh(): Promise<boolean> {
    console.debug("Starting token refresh");
    this.state.isRefreshing = true;

    try {
      // 在刷新开始时进行Cookie路径迁移检查（托底机制）
      try {
        cookieService.migrateCookiesToRootPath();
        console.debug("Cookie path migration completed during token refresh");
      } catch (error) {
        console.warn(
          "Cookie path migration failed during token refresh:",
          error,
        );
      }

      const refreshToken = cookieService.get("refresh_token");
      if (!refreshToken) {
        console.warn("No refresh token available");
        return false;
      }

      // 检查refresh token是否即将过期
      const refreshExpiry = this.getTokenExpiration(refreshToken);
      const daysUntilExpiry =
        (refreshExpiry - Date.now()) / (1000 * 60 * 60 * 24);

      if (daysUntilExpiry < 0) {
        console.warn("Refresh token has expired");
        return false;
      }

      if (daysUntilExpiry < 0.5) {
        // 少于12小时
        console.warn(
          `Refresh token expires in ${Math.round(daysUntilExpiry * 24)} hours`,
        );
      }

      // 调用刷新API
      const response = await api.post<TokenResponse>("/token_refresh/", {
        refresh: refreshToken,
      });

      if (response.data?.access) {
        // 更新cookies
        cookieService.set("jwt_token", response.data.access, {
          expires: JWT_CONFIG.ACCESS_TOKEN_DAYS,
        });

        if (response.data.refresh) {
          cookieService.set("refresh_token", response.data.refresh, {
            expires: JWT_CONFIG.REFRESH_TOKEN_DAYS,
          });
        }

        this.state.lastRefreshTime = Date.now();

        // 重新调度下次刷新
        this.scheduleNextRefresh();

        console.log("Token refresh successful");
        reportMessage("JWT token刷新成功", "info");
        return true;
      }

      console.error("Refresh response missing access token");
      return false;
    } catch (error) {
      console.error("Token refresh failed:", error);
      reportError(error as Error, {
        type: "jwt_lifecycle_error",
        action: "refresh_token",
        context: "silent_background_refresh",
      });
      return false;
    } finally {
      this.state.isRefreshing = false;
    }
  }

  /**
   * 调度下次刷新
   */
  private scheduleNextRefresh(): void {
    this.clearAllTimers();

    const accessToken = cookieService.get("jwt_token");
    if (!accessToken) {
      console.debug("No access token, skipping refresh scheduling");
      return;
    }

    const tokenAge = this.getTokenAge(accessToken);
    if (tokenAge < 0) {
      console.warn("Invalid token age, skipping refresh scheduling");
      return;
    }

    let intervalMs: number;
    let description: string;

    if (tokenAge >= REFRESH_STRATEGY.CRITICAL_DAY) {
      // 第6-7天：最后机会，每30分钟
      intervalMs = REFRESH_STRATEGY.CRITICAL_INTERVAL_MINUTES * 60 * 1000;
      description = `critical (day ${Math.floor(tokenAge)}, every ${REFRESH_STRATEGY.CRITICAL_INTERVAL_MINUTES}min)`;
    } else if (tokenAge >= REFRESH_STRATEGY.URGENT_DAY) {
      // 第5天：紧急模式，每2小时
      intervalMs = REFRESH_STRATEGY.URGENT_INTERVAL_HOURS * 60 * 60 * 1000;
      description = `urgent (day ${Math.floor(tokenAge)}, every ${REFRESH_STRATEGY.URGENT_INTERVAL_HOURS}h)`;
    } else if (tokenAge >= REFRESH_STRATEGY.NORMAL_DAY) {
      // 第4天：正常模式，每6小时
      intervalMs = REFRESH_STRATEGY.NORMAL_INTERVAL_HOURS * 60 * 60 * 1000;
      description = `normal (day ${Math.floor(tokenAge)}, every ${REFRESH_STRATEGY.NORMAL_INTERVAL_HOURS}h)`;
    } else if (tokenAge >= REFRESH_STRATEGY.START_DAY) {
      // 第3天：开始模式，每12小时
      intervalMs = REFRESH_STRATEGY.START_INTERVAL_HOURS * 60 * 60 * 1000;
      description = `start (day ${Math.floor(tokenAge)}, every ${REFRESH_STRATEGY.START_INTERVAL_HOURS}h)`;
    } else {
      // 前3天：不需要刷新，等到第3天
      const timeUntilStart =
        (REFRESH_STRATEGY.START_DAY - tokenAge) * 24 * 60 * 60 * 1000;
      intervalMs = Math.max(timeUntilStart, 60 * 60 * 1000); // 最少1小时后检查
      description = `waiting (day ${Math.floor(tokenAge)}, next check in ${Math.round(intervalMs / (60 * 60 * 1000))}h)`;
    }

    const timer = setTimeout(() => {
      this.silentBackgroundRefresh().then((success) => {
        if (success) {
          this.scheduleNextRefresh(); // 成功后继续调度
        } else {
          // 失败后稍后重试
          const retryTimer = setTimeout(
            () => {
              this.scheduleNextRefresh();
            },
            5 * 60 * 1000,
          ); // 5分钟后重试
          this.state.timers.push(retryTimer);
        }
      });
    }, intervalMs);

    this.state.timers.push(timer);
    console.debug(`Next refresh scheduled: ${description}`);
  }

  /**
   * 立即调度刷新（用户活动触发）
   */
  private scheduleImmediateRefresh(): void {
    if (this.state.isRefreshing) return;

    const timer = setTimeout(() => {
      this.silentBackgroundRefresh();
    }, 1000); // 1秒后执行，避免频繁触发

    this.state.timers.push(timer);
    console.debug("Immediate refresh scheduled due to user activity");
  }

  /**
   * 开始监听用户活动
   */
  private startActivityMonitoring(): void {
    const activityEvents = ["click", "keydown", "scroll", "touchstart"];
    const throttledActivity = this.throttle(() => this.onUserActivity(), 60000); // 1分钟最多触发一次

    activityEvents.forEach((event) => {
      document.addEventListener(event, throttledActivity, { passive: true });
    });

    console.debug("User activity monitoring started");
  }

  /**
   * 停止监听用户活动
   */
  private stopActivityMonitoring(): void {
    // 移除所有事件监听器
    const activityEvents = ["click", "keydown", "scroll", "touchstart"];
    activityEvents.forEach((event) => {
      document.removeEventListener(event, this.onUserActivity);
    });
  }

  /**
   * 清除所有定时器
   */
  private clearAllTimers(): void {
    this.state.timers.forEach((timer) => clearTimeout(timer));
    this.state.timers = [];
  }

  /**
   * 获取token的年龄（天数）
   */
  private getTokenAge(token: string): number {
    try {
      const payload = this.parseJWTPayload(token);
      if (!payload?.exp) return -1;

      // 如果有 iat 字段，使用它
      if (payload.iat) {
        const issuedTime = payload.iat * 1000; // 转换为毫秒
        const ageMs = Date.now() - issuedTime;
        return ageMs / (1000 * 60 * 60 * 24); // 转换为天数
      }

      // 如果没有 iat 字段，使用 exp 推算（假设 access token 有效期为5天）
      const expTime = payload.exp * 1000;
      const estimatedIssuedTime =
        expTime - JWT_CONFIG.ACCESS_TOKEN_DAYS * 24 * 60 * 60 * 1000;
      const ageMs = Date.now() - estimatedIssuedTime;
      return Math.max(0, ageMs / (1000 * 60 * 60 * 24)); // 不能小于0
    } catch {
      return -1;
    }
  }

  /**
   * 获取token的过期时间
   */
  private getTokenExpiration(token: string): number {
    try {
      const payload = this.parseJWTPayload(token);
      if (!payload?.exp) return 0;
      return payload.exp * 1000; // 转换为毫秒
    } catch {
      return 0;
    }
  }

  /**
   * 解析JWT payload
   */
  private parseJWTPayload(token: string): any {
    try {
      const base64Payload = token.split(".")[1];
      const normalizedPayload = base64Payload
        .replace(/-/g, "+")
        .replace(/_/g, "/");
      const padding = "=".repeat((4 - (normalizedPayload.length % 4)) % 4);
      const decodedPayload = decodeURIComponent(
        escape(atob(normalizedPayload + padding)),
      );
      return JSON.parse(decodedPayload);
    } catch (error) {
      console.error("Failed to parse JWT payload:", error);
      return null;
    }
  }

  /**
   * 节流函数
   */
  private throttle<T extends (...args: any[]) => any>(
    func: T,
    limit: number,
  ): (...args: Parameters<T>) => void {
    let inThrottle: boolean;
    return function (this: any, ...args: Parameters<T>) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }

  /**
   * 获取当前状态（调试用）
   */
  public getStatus(): {
    isStarted: boolean;
    isRefreshing: boolean;
    lastRefreshTime: number;
    lastActivityTime: number;
    activeTimers: number;
    tokenAge?: number;
  } {
    const accessToken = cookieService.get("jwt_token");
    return {
      isStarted: this.isStarted,
      isRefreshing: this.state.isRefreshing,
      lastRefreshTime: this.state.lastRefreshTime,
      lastActivityTime: this.state.lastActivityTime,
      activeTimers: this.state.timers.length,
      tokenAge: accessToken ? this.getTokenAge(accessToken) : undefined,
    };
  }
}

// 导出单例实例
export const jwtLifecycleService = new JWTLifecycleService();

// 导出类和配置以便测试
export { JWTLifecycleService, REFRESH_STRATEGY };
